import { Component, Input, inject, computed, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, signal, input, OnDestroy, DoCheck, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { ProductConfigService } from '../../../../core/services/product-config.service';
import { jsPDF } from 'jspdf';

export interface TranscriptionMessage {
  sender: string;
  time: string;
  content: string;
  role: 'AI' | 'AGENT' | 'CUSTOMER' | 'SYSTEM';
  avatar?: string;
  formattedTime?: string;
}

import { MarkdownModule, provideMarkdown } from 'ngx-markdown';

@Component({
  selector: 'app-call-transcription',
  standalone: true,
  imports: [CommonModule, IconComponentV2, MarkdownModule],
  providers: [provideMarkdown()],
  templateUrl: './call-transcription.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Markdown Content Styling - Light Theme */
    ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #1E1B4B;
      }
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.1em; }
      h4, h5, h6 { font-size: 1em; }

      /* First header has no top margin */
      h1:first-child, h2:first-child, h3:first-child,
      h4:first-child, h5:first-child, h6:first-child {
        margin-top: 0;
      }

      /* Paragraphs */
      p {
        margin-bottom: 0.8em;
        line-height: 1.6;
      }
      p:last-child {
        margin-bottom: 0;
      }

      /* Lists */
      ul, ol {
        list-style: disc;
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      li {
        margin-bottom: 0.3em;
        line-height: 1.5;
      }
      li:last-child {
        margin-bottom: 0;
      }
      
      /* Nested lists */
      ul ul, ol ul, ul ol, ol ol {
        margin-top: 0.3em;
        margin-bottom: 0.3em;
      }

      /* Emphasis */
      strong {
        font-weight: 600;
        color: #4F46E5;
      }
      em {
        font-style: italic;
      }

      /* Code */
      code {
        background: #EDE9FE;
        padding: 0.15em 0.4em;
        border-radius: 0.25em;
        font-size: 0.9em;
        font-family: 'Courier New', monospace;
        color: #6366F1;
      }
      pre {
        background: #EDE9FE;
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }

      /* Links */
      a {
        color: #6366F1;
        text-decoration: underline;
      }
      a:hover {
        color: #4F46E5;
      }
    }

    /* Markdown Content Styling - Dark Theme */
    :host-context(.dark) ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        color: #BAE6FD;
      }
      p {
        color: #CBD5E1;
      }
      strong {
        color: #A78BFA;
      }
      code, pre {
        background: #5B21B6;
        color: #E9D5FF;
      }
      a {
        color: #818CF8;
      }
      a:hover {
        color: #A78BFA;
      }
    }
  `]
})
export class CallTranscriptionComponent implements OnChanges, OnDestroy, DoCheck {
  @Input({ required: true }) messages: TranscriptionMessage[] = [];
  @Input() isChatView: boolean = false;
  @Input() productName: string = '';
  @Input() meetingUrl: string = '';
  @Input() dateTime: string = '';
  @Input() agents: string = '';
  @Input() isPlaying: boolean = false;
  @Input() isMuted: boolean = false;
  @Input() currentTime: number = 0;
  @Input() audioDuration: number = 0;
  @Input() activeTranscriptIndex: number = -1;
  @Input() hideHeader: boolean = false;
  @Input() downloadTitle: string = 'Transcription';
  @Input() downloadFileName: string = 'transcript.pdf';

  /** Scrolls to the true container bottom (including padding) on new messages. */
  autoScrollToBottom = input(false);

  @Output() togglePlay = new EventEmitter<void>();
  @Output() toggleMute = new EventEmitter<void>();
  @Output() seek = new EventEmitter<number>();
  @Output() download = new EventEmitter<void>();
  @Output() stopPlayback = new EventEmitter<void>();

  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  isAutoScrolling = signal(true);
  scrollDirection = signal<'up' | 'down'>('down');
  private _isProgrammaticScroll = false;

  private _productConfigService = inject(ProductConfigService);

  productConfig = computed(() => {
    return this._productConfigService.getProductConfig(this.productName);
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTranscriptIndex'] && this.activeTranscriptIndex !== -1) {
      if (this.isAutoScrolling()) {
        this.scrollToActiveTranscript(this.activeTranscriptIndex);
      } else {
        this.updateScrollDirection();
      }
    }

    if (changes['messages'] && this.isChatView && this.messages?.length) {
      this.scrollToLatestMessage();
    }
  }

  getInitials(name: string): string {
    if (!name) return 'NA';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  formatTime(seconds: number | undefined): string {
    if (seconds === undefined || isNaN(seconds)) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  onSeek(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    this.seek.emit(percentage);
  }

  handleScroll(_event: Event): void {
    if (this._isProgrammaticScroll || this.isChatView) return;
    this.updateScrollDirection();
  }

  ngDoCheck(): void {
    if (!this.isAutoScrolling()) {
      this.updateScrollDirection();
    }
  }

  private updateScrollDirection(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) return;

    if (this.isChatView) return;

    if (this.activeTranscriptIndex === -1) return;

    const elements = container.querySelectorAll('.transcript-wrapper');
    const targetElement = elements[this.activeTranscriptIndex] as HTMLElement;

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const isAbove = rect.top < window.innerHeight / 2;

      const newDirection = isAbove ? 'up' : 'down';
      if (this.scrollDirection() !== newDirection) {
        this.scrollDirection.set(newDirection);
      }
    }
  }

  manualScroll(): void {
    if (this.isChatView) return;
    if (this.isAutoScrolling()) {
      this.isAutoScrolling.set(false);
    }
  }

  resumeAutoScroll(): void {
    this.isAutoScrolling.set(true);
    if (this.activeTranscriptIndex !== -1) {
      this.scrollToActiveTranscript(this.activeTranscriptIndex);
    }
  }

  showResumeScrollButton(): boolean {
    if (this.isChatView || this.isAutoScrolling()) return false;
    return this.activeTranscriptIndex !== -1;
  }

  resumeScrollLabel(): string {
    return 'Go to current transcript';
  }

  private scrollToLatestMessage(): void {
    if (this.autoScrollToBottom()) {
      this.scrollToContainerBottom();
      return;
    }

    setTimeout(() => {
      const container = this.scrollContainer?.nativeElement;
      if (!container) return;

      const elements = container.querySelectorAll('.transcript-wrapper');
      const lastMessage = elements[elements.length - 1] as HTMLElement | undefined;

      this._isProgrammaticScroll = true;
      if (lastMessage) {
        lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
      setTimeout(() => {
        this._isProgrammaticScroll = false;
      }, 400);
    }, 0);
  }

  private scrollToContainerBottom(): void {
    const scrollToBottom = (): void => {
      const container = this.scrollContainer?.nativeElement;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
    };

    this._isProgrammaticScroll = true;
    requestAnimationFrame(() => {
      scrollToBottom();
      requestAnimationFrame(() => {
        scrollToBottom();
        setTimeout(() => {
          scrollToBottom();
          this._isProgrammaticScroll = false;
        }, 150);
      });
    });
  }

  ngOnDestroy(): void {
    if (this.isPlaying) {
      this.stopPlayback.emit();
    }
  }

  private scrollToActiveTranscript(index: number): void {
    setTimeout(() => {
      const container = this.scrollContainer?.nativeElement;
      if (!container) return;
      
      const elements = container.querySelectorAll('.transcript-wrapper');
      const targetElement = elements[index] as HTMLElement;
      
      if (targetElement) {
        this._isProgrammaticScroll = true;
        
        // Center the message in the view using block: 'center'
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        // Reset the flag after smooth scroll duration
        setTimeout(() => {
          this._isProgrammaticScroll = false;
        }, 600);
      }
    }, 100); // Small delay to ensure DOM is ready
  }

  formatMessage(text: string): string {
    if (!text) return '';
    // Replace literal '\n' string with actual newline character
    // Also handle possible variations like '\r\n' if they appear as literals
    return text.replace(/\\n/g, '\n');
  }

  getRoleStyles(role: string): string {
    switch (role) {
      case 'AI':
        return 'bg-purple-50/50 border-purple-200 ring-1 ring-purple-100';
      case 'AGENT':
        return 'bg-slate-50 border-slate-100';
      default:
        return 'bg-white border-slate-100';
    }
  }

  downloadTranscript() {
    const pdf = new jsPDF('p', 'pt', 'a4');
    let yPos = 40;
    const margin = 40;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxLineWidth = pageWidth - margin * 2;
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Add document title
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(31, 41, 55);
    pdf.text(this.downloadTitle || 'Transcription', margin, yPos);
    yPos += 30;
    
    for (const msg of this.messages) {
      let text = msg.content || '';
      // Strip HTML tags for clean text
      text = text.replace(/<[^>]*>?/gm, '');
      text = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
      text = text.replace(/\\n/g, '\n');
      text = text.trim();
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10.5);
      
      if (msg.role === 'AI') {
        const textIndent = 30; 
        const textSpace = maxLineWidth;
        const lines = pdf.splitTextToSize(text, textSpace);
        
        const blockBottomRel = 36 + (lines.length > 0 ? lines.length - 1 : 0) * 15 + 14;
        const boxStartRel = -10;
        const blockHeight = blockBottomRel - boxStartRel;
        
        if (yPos + blockHeight > pageHeight - margin) { 
          pdf.addPage(); 
          yPos = margin + 10; 
        }
        
        const headerY = yPos + 16;
        const textYStart = yPos + 34;
        const boxStartY = yPos - 10;
        const blockBottomY = yPos + blockBottomRel;
        
        pdf.setDrawColor(233, 213, 255); 
        pdf.setFillColor(253, 242, 248); 
        pdf.roundedRect(margin - 10, boxStartY, maxLineWidth + 20, blockHeight, 10, 10, 'FD');
        
        // Avatar Circle (Small for AI)
        pdf.setFillColor(139, 92, 246); 
        const avatarR = 11; 
        const avatarCx = margin + avatarR;
        const avatarCy = yPos + avatarR;
        pdf.circle(avatarCx, avatarCy, avatarR, 'F');
        
        // Draw Bot Icon
        pdf.setDrawColor(255, 255, 255);
        const s = 0.65; 
        const dx = avatarCx - 12 * s;
        const dy = avatarCy - 13.5 * s;
        
        pdf.setLineWidth(1.8 * s);
        pdf.roundedRect(dx + 3*s, dy + 8*s, 18*s, 14*s, 3*s, 3*s, 'S');
        pdf.line(dx + 12*s, dy + 8*s, dx + 12*s, dy + 5*s);
        pdf.line(dx + 12*s, dy + 5*s, dx + 9*s, dy + 5*s);
        pdf.line(dx + 1.5*s, dy + 15*s, dx + 3*s, dy + 15*s);
        pdf.line(dx + 21*s, dy + 15*s, dx + 22.5*s, dy + 15*s);
        pdf.setLineWidth(2.2 * s);
        pdf.line(dx + 9*s, dy + 13*s, dx + 9*s, dy + 15.5*s);
        pdf.line(dx + 15*s, dy + 13*s, dx + 15*s, dy + 15.5*s);
        
        const headerX = margin + textIndent;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10.5);
        pdf.setTextColor(79, 70, 229); 
        const senderName = msg.sender || 'AI';
        pdf.text(senderName, headerX, headerY);
        
        const nameWidth = pdf.getTextWidth(senderName);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(156, 163, 175);
        const timeText = `[${msg.formattedTime || msg.time || '0:00'}]`;
        pdf.text(timeText, headerX + nameWidth + 5, headerY);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(30, 27, 75);
        let currentTextY = textYStart;
        for (const line of lines) {
          pdf.text(line, margin, currentTextY);
          currentTextY += 15;
        }
        yPos = blockBottomY + 20;
        
      } else if (msg.role === 'AGENT') {
        const textSpace = maxLineWidth * 0.8;
        const lines = pdf.splitTextToSize(text, textSpace);
        let maxTextW = 0;
        for (const line of lines) {
          const w = pdf.getTextWidth(line);
          if (w > maxTextW) maxTextW = w;
        }
        
        const blockBottomRel = 36 + (lines.length > 0 ? lines.length - 1 : 0) * 15 + 14;
        const blockHeight = blockBottomRel;
        
        if (yPos + blockHeight > pageHeight - margin) { 
          pdf.addPage(); 
          yPos = margin + 10; 
        }
        
        const headerY = yPos + 18;
        const textYStart = yPos + 36;
        const bubbleStartY = yPos + 22; 
        const bubbleBottomY = yPos + blockBottomRel;
        const bubbleHeight = bubbleBottomY - bubbleStartY;
        
        // Avatar Circle (Standard for Agent)
        const avatarR = 14; 
        const avatarCx = pageWidth - margin - avatarR;
        const avatarCy = yPos + avatarR;
        
        pdf.setFillColor(20, 20, 55); 
        pdf.circle(avatarCx, avatarCy, avatarR, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(255, 255, 255);
        const initials = this.getInitials(msg.sender);
        const initialsW = pdf.getTextWidth(initials);
        pdf.text(initials, avatarCx - (initialsW / 2), avatarCy + 4);
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(20, 20, 55); 
        const senderName = msg.sender || 'Unknown';
        const nameWidth = pdf.getTextWidth(senderName);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(156, 163, 175); 
        const timeText = `[${msg.formattedTime || msg.time || '0:00'}]`;
        const timeWidth = pdf.getTextWidth(timeText);
        
        const headerRightSide = avatarCx - avatarR - 10;
        const nameX = headerRightSide - nameWidth;
        
        pdf.text(senderName, nameX, headerY);
        pdf.text(timeText, nameX - timeWidth - 6, headerY);
        
        const bubbleRightSide = headerRightSide;
        const bubbleW = Math.max(nameWidth + timeWidth + 10, maxTextW + 24);
        const bubbleLeftSide = bubbleRightSide - bubbleW;
        
        pdf.setFillColor(238, 242, 255); 
        pdf.roundedRect(bubbleLeftSide, bubbleStartY, bubbleW, bubbleHeight, 8, 8, 'F');
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10.5);
        pdf.setTextColor(20, 20, 55); 
        let currentTextY = textYStart;
        for (const line of lines) {
          pdf.text(line, bubbleLeftSide + 12, currentTextY);
          currentTextY += 15;
        }
        yPos = bubbleBottomY + 20;
        
      } else {
        // CUSTOMER role
        const textSpace = maxLineWidth * 0.8;
        const lines = pdf.splitTextToSize(text, textSpace);
        let maxTextW = 0;
        for (const line of lines) {
          const w = pdf.getTextWidth(line);
          if (w > maxTextW) maxTextW = w;
        }
        
        const blockBottomRel = 36 + (lines.length > 0 ? lines.length - 1 : 0) * 15 + 14;
        const blockHeight = blockBottomRel;
        
        if (yPos + blockHeight > pageHeight - margin) { 
          pdf.addPage(); 
          yPos = margin + 10; 
        }
        
        const headerY = yPos + 18;
        const textYStart = yPos + 36;
        const bubbleStartY = yPos + 22; 
        const bubbleBottomY = yPos + blockBottomRel;
        const bubbleHeight = bubbleBottomY - bubbleStartY;
        
        // Avatar Circle (Standard for Customer)
        const avatarR = 14; 
        const avatarCx = margin + avatarR;
        const avatarCy = yPos + avatarR;
        
        pdf.setFillColor(37, 99, 235); 
        pdf.circle(avatarCx, avatarCy, avatarR, 'F');
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(255, 255, 255);
        const initials = this.getInitials(msg.sender);
        const initialsW = pdf.getTextWidth(initials);
        pdf.text(initials, avatarCx - (initialsW / 2), avatarCy + 4);
        
        const headerX = margin + avatarR * 2 + 10;
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(20, 20, 55); 
        const senderName = msg.sender || 'Unknown';
        pdf.text(senderName, headerX, headerY);
        
        const nameWidth = pdf.getTextWidth(senderName);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(156, 163, 175); 
        const timeText = `[${msg.formattedTime || msg.time || '0:00'}]`;
        pdf.text(timeText, headerX + nameWidth + 6, headerY);
        
        const bubbleW = Math.max(nameWidth + pdf.getTextWidth(timeText) + 10, maxTextW + 24);
        
        pdf.setFillColor(248, 250, 252); 
        pdf.roundedRect(headerX - 10, bubbleStartY, bubbleW, bubbleHeight, 8, 8, 'F');
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10.5);
        pdf.setTextColor(20, 20, 55); 
        let currentTextY = textYStart;
        for (const line of lines) {
          pdf.text(line, headerX + 2, currentTextY);
          currentTextY += 15;
        }
        yPos = bubbleBottomY + 20;
      }
    }
    
    pdf.save(this.downloadFileName || 'transcript.pdf');
  }

}
